import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LogOut, Trash2, Save, Plus, Mail, AlertTriangle, UserCircle, KeyRound, Users, ShieldCheck, Search, LockKeyhole, UserCog, RefreshCw, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { AppointmentsManager } from "./AppointmentsManager";
import { ConsultationTypesManager } from "./ConsultationTypesManager";
import { PrescriptionsManager } from "./PrescriptionsManager";
import { QuestionnairesManager } from "./QuestionnairesManager";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const STATUSES = ["reserved", "pending_payment", "confirmed", "cancelled"];
const STAFF_ROLES = [
  { value: "admin", label: "Admin", description: "Full dashboard, settings and user management" },
  { value: "physician", label: "Physician", description: "Clinical work, prescriptions and patient forms" },
  { value: "reception", label: "Reception", description: "Appointments, intake and front desk work" },
];
const PASSWORD_RULES = [
  { id: "min-length", label: "At least 10 characters", check: (value) => value.length >= 10 },
  { id: "uppercase", label: "One uppercase letter", check: (value) => /[A-Z]/.test(value) },
  { id: "lowercase", label: "One lowercase letter", check: (value) => /[a-z]/.test(value) },
  { id: "number", label: "One number", check: (value) => /\d/.test(value) },
  { id: "special", label: "One special character", check: (value) => /[^A-Za-z0-9]/.test(value) },
];
const ROLE_LABELS = Object.fromEntries(STAFF_ROLES.map((role) => [role.value, role.label]));
const formatAccountDate = (iso) => {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [types, setTypes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [blockers, setBlockers] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [nb, setNb] = useState({ start_date: "", end_date: "", reason: "" });
  const [savingBookingEnabled, setSavingBookingEnabled] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/bookings").then(({ data }) => setBookings(data));
    api.get("/admin/consultation-types").then(({ data }) => setTypes(data));
    api.get("/admin/settings").then(({ data }) => setSettings(data));
    api.get("/admin/blockers").then(({ data }) => setBlockers(data));
    api.get("/admin/prescriptions").then(({ data }) => setPrescriptions(data)).catch(() => {});
    api.get("/admin/questionnaires").then(({ data }) => setQuestionnaires(data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  const updateBooking = async (id, patch) => {
    const { data } = await api.patch(`/admin/bookings/${id}`, patch);
    setBookings((b) => b.map((x) => (x.id === id ? data : x)));
    toast.success("Appointment updated");
    return data;
  };
  const deleteBooking = async (id) => { await api.delete(`/admin/bookings/${id}`); setBookings((b) => b.filter((x) => x.id !== id)); toast.success("Appointment removed"); };
  const createBooking = async (payload) => {
    const { data } = await api.post("/admin/bookings", payload);
    setBookings((b) => [...b, data]);
    toast.success("Appointment created");
    return data;
  };
  const updatePrescription = async (id, patch) => {
    const { data } = await api.patch(`/admin/prescriptions/${id}`, patch);
    setPrescriptions((r) => r.map((x) => (x.id === id ? data : x)));
    toast.success("Prescription updated");
    return data;
  };

  const saveType = async (t) => {
    const { data } = await api.patch(`/admin/consultation-types/${t.id}`, {
      fee: Number(t.fee), deposit: 0, duration: Number(t.duration),
      format: t.format, active: t.active, payment_required: false,
    });
    setTypes((ts) => ts.map((x) => (x.id === t.id ? data : x)));
    toast.success("Consultation updated");
  };
  const setType = (id, k, v) => setTypes((ts) => ts.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  const bookingEnabled = settings?.booking_enabled !== false;
  const toggleBookingEnabled = async () => {
    const nextEnabled = !bookingEnabled;
    setSavingBookingEnabled(true);
    try {
      const { data } = await api.put("/admin/settings", { booking_enabled: nextEnabled });
      setSettings(data);
      toast.success(nextEnabled ? "Online reservations enabled" : "Online reservations paused");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reservation setting could not be changed");
    } finally {
      setSavingBookingEnabled(false);
    }
  };

  const toggleDay = (d) => {
    const wd = settings.workdays.includes(d) ? settings.workdays.filter((x) => x !== d) : [...settings.workdays, d];
    setSettings({ ...settings, workdays: wd });
  };
  const saveSettings = async () => {
    const dh = {};
    settings.workdays.forEach((d) => { dh[String(d)] = getRanges(d); });
    const { data } = await api.put("/admin/settings", {
      workdays: settings.workdays, open_hour: Number(settings.open_hour) || 10,
      close_hour: Number(settings.close_hour) || 18, step_min: Number(settings.step_min),
      day_hours: dh,
    });
    setSettings(data); toast.success("Availability saved");
  };
  const toHHMM = (v) => (typeof v === "number" ? `${String(v).padStart(2, "0")}:00` : v || "09:00");
  const getRanges = (d) => {
    const dh = settings.day_hours?.[String(d)];
    if (Array.isArray(dh)) return dh.length ? dh : [{ start: "09:00", end: "12:00" }];
    if (dh && (dh.open != null || dh.close != null)) return [{ start: toHHMM(dh.open ?? settings.open_hour), end: toHHMM(dh.close ?? settings.close_hour) }];
    return [{ start: toHHMM(settings.open_hour ?? 10), end: toHHMM(settings.close_hour ?? 18) }];
  };
  const setRanges = (d, ranges) => setSettings((s) => ({ ...s, day_hours: { ...(s.day_hours || {}), [String(d)]: ranges } }));
  const addRange = (d) => setRanges(d, [...getRanges(d), { start: "13:00", end: "16:00" }]);
  const updateRange = (d, i, field, v) => setRanges(d, getRanges(d).map((x, j) => (j === i ? { ...x, [field]: v } : x)));
  const removeRange = (d, i) => setRanges(d, getRanges(d).filter((_, j) => j !== i));
  const addBlocker = async () => {
    if (!nb.start_date || !nb.end_date) return toast.error("Choose start and end dates");
    const { data } = await api.post("/admin/blockers", nb);
    setBlockers((b) => [...b, data]); setNb({ start_date: "", end_date: "", reason: "" }); toast.success("Vacation / blocker added");
  };
  const delBlocker = async (id) => { await api.delete(`/admin/blockers/${id}`); setBlockers((b) => b.filter((x) => x.id !== id)); };

  const statusColor = (s) => s === "confirmed" ? "bg-champagne-gold text-deep-ink" : s === "pending_payment" ? "bg-terracotta/20 text-terracotta" : s === "cancelled" ? "bg-deep-ink/10 text-deep-ink/50" : "bg-muted-sage/30 text-deep-ink";

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <Toaster position="top-center" />
      <header className="flex items-center justify-between border-b border-border bg-warm-ivory px-5 py-4 lg:px-10">
        <Logo />
        <div className="flex items-center gap-4">
          <AccountDialog user={user} />
          <Button data-testid="admin-logout" variant="ghost" onClick={() => { logout(); navigate("/admin/login"); }} className="gap-2 text-deep-ink">
            <LogOut className="h-4 w-4" />Logout
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8 lg:px-10">
        <h1 className="font-serif text-3xl text-deep-ink lg:text-4xl">Practice dashboard</h1>

        {settings && (
          <div className={`mt-6 flex flex-wrap items-center justify-between gap-4 rounded-3xl border p-5 ${bookingEnabled ? "border-muted-sage/30 bg-muted-sage/10" : "border-terracotta/30 bg-terracotta/10"}`} data-testid="online-booking-control">
            <div>
              <p className="text-xs font-semibold uppercase tracking-editorial text-deep-ink/50">Online reservations</p>
              <p className="mt-1 font-serif text-2xl text-deep-ink">{bookingEnabled ? "Active" : "Paused"}</p>
              <p className="mt-1 text-sm text-deep-ink/60">
                {bookingEnabled ? "Patients can reserve available appointments online." : "The public booking form is closed. Existing appointments remain unchanged."}
              </p>
            </div>
            <Button
              type="button"
              onClick={toggleBookingEnabled}
              disabled={savingBookingEnabled}
              data-testid="toggle-online-booking"
              className={`gap-2 rounded-full ${bookingEnabled ? "bg-terracotta text-warm-ivory hover:bg-deep-ink" : "bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink"}`}
            >
              {bookingEnabled ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
              {savingBookingEnabled ? "Saving…" : bookingEnabled ? "Pause reservations" : "Enable reservations"}
            </Button>
          </div>
        )}

        {stats && (
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[["Total", stats.total], ["Confirmed", stats.confirmed], ["Pending", stats.pending]].map(([l, v]) => (
              <div key={l} className="rounded-2xl bg-warm-ivory p-5"><p className="text-xs uppercase tracking-editorial text-deep-ink/50">{l}</p><p className="mt-1 font-serif text-3xl text-deep-ink">{v}</p></div>
            ))}
            <div className={`rounded-2xl p-5 ${stats.smtp_configured ? "bg-warm-ivory" : "bg-terracotta/10"}`}>
              <p className="flex items-center gap-1 text-xs uppercase tracking-editorial text-deep-ink/50"><Mail className="h-3 w-3" />Email</p>
              <p className="mt-1 text-sm font-medium text-deep-ink">{stats.smtp_configured ? "SMTP ready" : "SMTP not set"}</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="bookings" className="mt-8">
          <TabsList className="bg-warm-ivory">
            <TabsTrigger value="bookings" data-testid="tab-bookings">Appointments</TabsTrigger>
            <TabsTrigger value="prescriptions" data-testid="tab-prescriptions">Prescriptions</TabsTrigger>
            <TabsTrigger value="questionnaires" data-testid="tab-questionnaires">Questionnaires</TabsTrigger>
            <TabsTrigger value="types" data-testid="tab-types">Consultations & prices</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">Availability & vacation</TabsTrigger>
          </TabsList>

          {/* APPOINTMENTS */}
          <TabsContent value="bookings" className="mt-6">
            <AppointmentsManager bookings={bookings} types={types} onUpdate={updateBooking} onDelete={deleteBooking} onCreate={createBooking} />
          </TabsContent>

          {/* QUESTIONNAIRES */}
          <TabsContent value="questionnaires" className="mt-6">
            <QuestionnairesManager submissions={questionnaires} />
          </TabsContent>

          {/* PRESCRIPTIONS */}
          <TabsContent value="prescriptions" className="mt-6">
            <PrescriptionsManager prescriptions={prescriptions} onUpdate={updatePrescription} />
          </TabsContent>

          {/* TYPES */}
          <TabsContent value="types" className="mt-6">
            <ConsultationTypesManager types={types} onReload={loadAll} />
          </TabsContent>

          {/* AVAILABILITY */}
          <TabsContent value="availability" className="mt-6 space-y-6">
            {settings && (
              <div className="rounded-3xl bg-warm-ivory p-6">
                <h3 className="font-serif text-xl text-deep-ink">Working days & hours</h3>
                <p className="mt-1 text-sm text-deep-ink/60">Enable each day you offer appointments and set its own opening and closing hour.</p>
                <div className="mt-4 space-y-2">
                  {DAYS.map(([lbl, d]) => {
                    const on = settings.workdays.includes(d);
                    const ranges = getRanges(d);
                    return (
                      <div key={d} className={`rounded-2xl px-4 py-3 transition-colors ${on ? "bg-sand" : "bg-sand/40"}`} data-testid={`day-row-${d}`}>
                        <div className="flex flex-wrap items-start gap-4">
                          <label className="flex w-40 items-center gap-2 pt-1.5 text-sm font-medium text-deep-ink">
                            <Switch checked={on} onCheckedChange={() => toggleDay(d)} data-testid={`day-${d}`} />{lbl}
                          </label>
                          {on ? (
                            <div className="flex-1 space-y-2">
                              {ranges.map((r, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-deep-ink/70" data-testid={`range-${d}-${i}`}>
                                  <Input type="time" value={r.start} onChange={(e) => updateRange(d, i, "start", e.target.value)} data-testid={`range-start-${d}-${i}`} className="w-32 bg-warm-ivory" />
                                  <span>–</span>
                                  <Input type="time" value={r.end} onChange={(e) => updateRange(d, i, "end", e.target.value)} data-testid={`range-end-${d}-${i}`} className="w-32 bg-warm-ivory" />
                                  {ranges.length > 1 && (
                                    <button onClick={() => removeRange(d, i)} className="text-terracotta hover:opacity-70" data-testid={`range-remove-${d}-${i}`}><Trash2 className="h-4 w-4" /></button>
                                  )}
                                </div>
                              ))}
                              <button onClick={() => addRange(d)} className="inline-flex items-center gap-1 text-xs font-medium text-champagne-gold hover:underline" data-testid={`range-add-${d}`}>
                                <Plus className="h-3.5 w-3.5" />Add time window
                              </button>
                            </div>
                          ) : (
                            <span className="pt-1.5 text-sm text-deep-ink/40">Closed</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 grid max-w-xs gap-4">
                  <LabeledNum label="Slot length (minutes)" value={settings.step_min} onChange={(v) => setSettings({ ...settings, step_min: v })} testid="step-min" />
                </div>
                <Button onClick={saveSettings} data-testid="save-settings" className="mt-4 gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink"><Save className="h-4 w-4" />Save hours</Button>
              </div>
            )}

            <div className="rounded-3xl bg-warm-ivory p-6">
              <h3 className="flex items-center gap-2 font-serif text-xl text-deep-ink"><AlertTriangle className="h-5 w-5 text-terracotta" />Vacation & blocked dates</h3>
              <p className="mt-1 text-sm text-deep-ink/60">Blocked date ranges are removed from patient availability.</p>
              <div className="mt-4 flex flex-wrap items-end gap-3">
                <div><p className="mb-1 text-xs text-deep-ink/60">From</p><Input type="date" value={nb.start_date} onChange={(e) => setNb({ ...nb, start_date: e.target.value })} data-testid="blocker-start" className="bg-sand" /></div>
                <div><p className="mb-1 text-xs text-deep-ink/60">To</p><Input type="date" value={nb.end_date} onChange={(e) => setNb({ ...nb, end_date: e.target.value })} data-testid="blocker-end" className="bg-sand" /></div>
                <div className="flex-1"><p className="mb-1 text-xs text-deep-ink/60">Reason</p><Input value={nb.reason} onChange={(e) => setNb({ ...nb, reason: e.target.value })} placeholder="Vacation" data-testid="blocker-reason" className="bg-sand" /></div>
                <Button onClick={addBlocker} data-testid="add-blocker" className="gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink"><Plus className="h-4 w-4" />Add</Button>
              </div>
              <div className="mt-5 space-y-2">
                {blockers.map((b) => (
                  <div key={b.id} className="flex items-center justify-between rounded-xl bg-sand px-4 py-3 text-sm" data-testid={`blocker-${b.id}`}>
                    <span>{b.start_date} → {b.end_date} {b.reason && <span className="text-deep-ink/50">· {b.reason}</span>}</span>
                    <button onClick={() => delBlocker(b.id)} className="text-terracotta hover:opacity-70"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {blockers.length === 0 && <p className="text-sm text-deep-ink/40">No blocked dates.</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const LabeledNum = ({ label, value, onChange, testid }) => (
  <div>
    <p className="mb-1.5 text-sm text-deep-ink/70">{label}</p>
    <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="bg-sand" />
  </div>
);

const AccountDialog = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "reception" });
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);
  const [resettingUserId, setResettingUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [resetPasswords, setResetPasswords] = useState({});
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const isOwnerAdmin = user?.role === "admin";

  const loadUsers = useCallback(async () => {
    if (!isOwnerAdmin) return;
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("User list could not be loaded");
    } finally {
      setLoadingUsers(false);
    }
  }, [isOwnerAdmin]);

  useEffect(() => {
    if (open && isOwnerAdmin) loadUsers();
  }, [open, isOwnerAdmin, loadUsers]);

  const passwordValid = (password, confirm = null, person = {}) => {
    const base = PASSWORD_RULES.every((rule) => rule.check(password || ""));
    const lowered = (password || "").toLowerCase();
    const emailName = (person.email || "").split("@")[0]?.toLowerCase();
    const hasEmailName = emailName && emailName.length >= 4 && lowered.includes(emailName);
    const hasNamePart = (person.name || "").toLowerCase().split(/\s+/).some((part) => part.length >= 4 && lowered.includes(part));
    const matchOk = confirm == null || password === confirm;
    return base && !hasEmailName && !hasNamePart && matchOk;
  };

  const filteredUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return users.filter((staff) => {
      const matchesText = !needle || [staff.name, staff.email, staff.role].some((field) => (field || "").toLowerCase().includes(needle));
      const matchesRole = roleFilter === "all" || staff.role === roleFilter;
      const matchesStatus = statusFilter === "all" || (statusFilter === "active" ? staff.is_active !== false : staff.is_active === false);
      return matchesText && matchesRole && matchesStatus;
    });
  }, [users, query, roleFilter, statusFilter]);

  const userStats = useMemo(() => ({
    total: users.length,
    active: users.filter((staff) => staff.is_active !== false).length,
    disabled: users.filter((staff) => staff.is_active === false).length,
    admins: users.filter((staff) => staff.role === "admin").length,
  }), [users]);

  const updatePassword = async (e) => {
    e.preventDefault();
    if (!passwordValid(passwordForm.new_password, passwordForm.confirm_password, user || {})) return toast.error("Please follow all password rules");
    setSavingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password could not be updated");
    } finally {
      setSavingPassword(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!passwordValid(newUser.password, null, newUser)) return toast.error("Please follow all password rules");
    setCreatingUser(true);
    try {
      const { data } = await api.post("/admin/users", newUser);
      setUsers((current) => [data, ...current]);
      setNewUser({ name: "", email: "", password: "", role: "reception" });
      toast.success("User created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "User could not be created");
    } finally {
      setCreatingUser(false);
    }
  };

  const setUserField = (id, field, value) => {
    setUsers((current) => current.map((staff) => (staff.id === id ? { ...staff, [field]: value } : staff)));
  };

  const saveUser = async (staff) => {
    setSavingUserId(staff.id);
    try {
      const { data } = await api.patch(`/admin/users/${staff.id}`, {
        name: staff.name,
        email: staff.email,
        role: staff.role,
        is_active: staff.is_active !== false,
      });
      setUsers((current) => current.map((item) => (item.id === staff.id ? data : item)));
      toast.success("User updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "User could not be updated");
      loadUsers();
    } finally {
      setSavingUserId(null);
    }
  };

  const resetUserPassword = async (staff) => {
    const newPassword = resetPasswords[staff.id] || "";
    if (!passwordValid(newPassword, null, staff)) return toast.error("Please follow all password rules");
    setResettingUserId(staff.id);
    try {
      const { data } = await api.post(`/admin/users/${staff.id}/reset-password`, { new_password: newPassword });
      setUsers((current) => current.map((item) => (item.id === staff.id ? data : item)));
      setResetPasswords((current) => ({ ...current, [staff.id]: "" }));
      toast.success("Password reset");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password could not be reset");
    } finally {
      setResettingUserId(null);
    }
  };

  const deleteUser = async (staff) => {
    const confirmed = window.confirm(`Delete ${staff.name || staff.email}? This permanently removes the staff account.`);
    if (!confirmed) return;
    setDeletingUserId(staff.id);
    try {
      await api.delete(`/admin/users/${staff.id}`);
      setUsers((current) => current.filter((item) => item.id !== staff.id));
      setResetPasswords((current) => {
        const next = { ...current };
        delete next[staff.id];
        return next;
      });
      toast.success("User deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "User could not be deleted");
      loadUsers();
    } finally {
      setDeletingUserId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="admin-account-trigger"
          className="hidden items-center gap-2 rounded-full px-3 py-2 text-sm text-deep-ink/70 transition-colors hover:bg-sand hover:text-deep-ink sm:inline-flex"
        >
          <UserCircle className="h-4 w-4" />
          {user?.email}
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto bg-warm-ivory text-deep-ink">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl font-normal text-deep-ink">
            <UserCircle className="h-5 w-5 text-champagne-gold" />Profile & team access
          </DialogTitle>
          <DialogDescription>{user?.name || "Practice user"} · {user?.email}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="password" className="mt-2">
          <TabsList className="bg-sand">
            <TabsTrigger value="password" data-testid="account-tab-password">Password</TabsTrigger>
            {isOwnerAdmin && <TabsTrigger value="users" data-testid="account-tab-users">User management</TabsTrigger>}
          </TabsList>

          <TabsContent value="password" className="mt-5">
            <form onSubmit={updatePassword} className="rounded-2xl bg-sand p-5">
              <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
                <div>
                  <h3 className="flex items-center gap-2 font-serif text-xl text-deep-ink">
                    <KeyRound className="h-5 w-5 text-champagne-gold" />Reset password
                  </h3>
                  <p className="mt-1 text-sm text-deep-ink/55">Use a unique password for this practice account.</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <LabeledPassword
                      label="Current password"
                      value={passwordForm.current_password}
                      onChange={(v) => setPasswordForm((f) => ({ ...f, current_password: v }))}
                      testid="account-current-password"
                    />
                    <LabeledPassword
                      label="New password"
                      value={passwordForm.new_password}
                      onChange={(v) => setPasswordForm((f) => ({ ...f, new_password: v }))}
                      testid="account-new-password"
                    />
                    <LabeledPassword
                      label="Repeat password"
                      value={passwordForm.confirm_password}
                      onChange={(v) => setPasswordForm((f) => ({ ...f, confirm_password: v }))}
                      testid="account-confirm-password"
                    />
                  </div>
                  <Button type="submit" disabled={savingPassword} data-testid="account-save-password" className="mt-4 gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
                    <Save className="h-4 w-4" />{savingPassword ? "Saving…" : "Save password"}
                  </Button>
                </div>
                <PasswordRules password={passwordForm.new_password} confirm={passwordForm.confirm_password} person={user || {}} showMatch />
              </div>
            </form>
          </TabsContent>

          {isOwnerAdmin && (
            <TabsContent value="users" className="mt-5 space-y-5">
              <div className="grid gap-3 sm:grid-cols-4">
                {[["Total", userStats.total], ["Active", userStats.active], ["Disabled", userStats.disabled], ["Admins", userStats.admins]].map(([label, value]) => (
                  <div key={label} className="rounded-2xl bg-sand p-4">
                    <p className="text-xs uppercase tracking-editorial text-deep-ink/45">{label}</p>
                    <p className="mt-1 font-serif text-3xl text-deep-ink">{value}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={createUser} className="rounded-2xl bg-sand p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="flex items-center gap-2 font-serif text-xl text-deep-ink">
                      <Users className="h-5 w-5 text-champagne-gold" />Add user
                    </h3>
                    <p className="mt-1 text-sm text-deep-ink/55">Create staff accounts with a clear role and an initial secure password.</p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {STAFF_ROLES.map((role) => (
                      <div key={role.value} className="rounded-xl bg-warm-ivory px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-editorial text-deep-ink/50">{role.label}</p>
                        <p className="mt-1 text-xs text-deep-ink/55">{role.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_1fr_1fr_180px]">
                  <LabeledText label="Name" value={newUser.name} onChange={(v) => setNewUser((u) => ({ ...u, name: v }))} testid="new-user-name" />
                  <LabeledText label="Email" type="email" value={newUser.email} onChange={(v) => setNewUser((u) => ({ ...u, email: v }))} testid="new-user-email" />
                  <LabeledPassword label="Initial password" value={newUser.password} onChange={(v) => setNewUser((u) => ({ ...u, password: v }))} testid="new-user-password" />
                  <div>
                    <p className="mb-1.5 text-sm text-deep-ink/70">Role</p>
                    <Select value={newUser.role} onValueChange={(role) => setNewUser((u) => ({ ...u, role }))}>
                      <SelectTrigger data-testid="new-user-role" className="bg-warm-ivory">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STAFF_ROLES.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
                  <PasswordRules password={newUser.password} person={newUser} compact />
                  <Button type="submit" disabled={creatingUser} data-testid="create-admin-user" className="gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
                    <Plus className="h-4 w-4" />{creatingUser ? "Creating…" : "Create user"}
                  </Button>
                </div>
              </form>

              <div className="rounded-2xl bg-sand p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="flex items-center gap-2 font-serif text-xl text-deep-ink">
                      <ShieldCheck className="h-5 w-5 text-champagne-gold" />Practice users
                    </h3>
                    <p className="mt-1 text-sm text-deep-ink/55">Search, edit roles, deactivate access, reset passwords and delete staff accounts.</p>
                  </div>
                  <Button type="button" variant="outline" onClick={loadUsers} disabled={loadingUsers} className="gap-2 rounded-full border-deep-ink/15 bg-warm-ivory text-deep-ink">
                    <RefreshCw className={`h-4 w-4 ${loadingUsers ? "animate-spin" : ""}`} />Refresh
                  </Button>
                </div>

                <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_180px_180px]">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-deep-ink/35" />
                    <Input data-testid="admin-user-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or role" className="bg-warm-ivory pl-9" />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger data-testid="admin-user-role-filter" className="bg-warm-ivory"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All roles</SelectItem>
                      {STAFF_ROLES.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="admin-user-status-filter" className="bg-warm-ivory"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-4 space-y-3">
                  {loadingUsers && <p className="text-sm text-deep-ink/50">Loading users…</p>}
                  {!loadingUsers && filteredUsers.map((staff) => (
                    <div key={staff.id} className={`rounded-2xl border p-4 ${staff.is_active === false ? "border-terracotta/20 bg-terracotta/5" : "border-transparent bg-warm-ivory"}`} data-testid={`staff-user-${staff.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="rounded-full bg-sand p-2 text-deep-ink"><UserCog className="h-5 w-5" /></div>
                          <div>
                            <p className="font-medium text-deep-ink">{staff.name || "Unnamed user"}</p>
                            <p className="text-sm text-deep-ink/55">{staff.email}</p>
                            <p className="mt-1 text-xs text-deep-ink/45">Last login: {formatAccountDate(staff.last_login_at)} · Created: {formatAccountDate(staff.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className="bg-muted-sage/30 text-deep-ink">{ROLE_LABELS[staff.role] || staff.role}</Badge>
                          <Badge className={staff.is_active === false ? "bg-terracotta/15 text-terracotta" : "bg-champagne-gold text-deep-ink"}>{staff.is_active === false ? "Disabled" : "Active"}</Badge>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_1fr_170px_170px_auto] lg:items-end">
                        <LabeledText label="Name" value={staff.name || ""} onChange={(v) => setUserField(staff.id, "name", v)} testid={`staff-name-${staff.id}`} />
                        <LabeledText label="Email" type="email" value={staff.email || ""} onChange={(v) => setUserField(staff.id, "email", v)} testid={`staff-email-${staff.id}`} />
                        <div>
                          <p className="mb-1.5 text-sm text-deep-ink/70">Role</p>
                          <Select value={staff.role || "reception"} onValueChange={(role) => setUserField(staff.id, "role", role)}>
                            <SelectTrigger data-testid={`staff-role-${staff.id}`} className="bg-sand"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STAFF_ROLES.map((role) => <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <label className="flex h-10 items-center gap-2 rounded-md bg-sand px-3 text-sm text-deep-ink/70">
                          <Switch checked={staff.is_active !== false} onCheckedChange={(checked) => setUserField(staff.id, "is_active", checked)} data-testid={`staff-active-${staff.id}`} />
                          Active
                        </label>
                        <Button type="button" onClick={() => saveUser(staff)} disabled={savingUserId === staff.id} data-testid={`save-staff-user-${staff.id}`} className="gap-2 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
                          <Save className="h-4 w-4" />{savingUserId === staff.id ? "Saving…" : "Save"}
                        </Button>
                      </div>

                      <div className="mt-4 rounded-xl bg-sand p-3">
                        <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-start">
                          <div>
                            <p className="mb-1.5 flex items-center gap-2 text-sm font-medium text-deep-ink"><LockKeyhole className="h-4 w-4 text-champagne-gold" />Reset staff password</p>
                            <Input type="password" value={resetPasswords[staff.id] || ""} onChange={(e) => setResetPasswords((current) => ({ ...current, [staff.id]: e.target.value }))} placeholder="New secure password" data-testid={`reset-staff-password-${staff.id}`} className="bg-warm-ivory" />
                            {(resetPasswords[staff.id] || "").length > 0 && <div className="mt-2"><PasswordRules password={resetPasswords[staff.id] || ""} person={staff} compact /></div>}
                          </div>
                          <Button type="button" onClick={() => resetUserPassword(staff)} disabled={resettingUserId === staff.id} data-testid={`reset-password-button-${staff.id}`} className="gap-2 rounded-full bg-warm-ivory text-deep-ink hover:bg-champagne-gold">
                            <KeyRound className="h-4 w-4" />{resettingUserId === staff.id ? "Resetting…" : "Set password"}
                          </Button>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-terracotta/20 bg-terracotta/5 p-3">
                        <div>
                          <p className="text-sm font-medium text-terracotta">Delete user</p>
                          <p className="text-xs text-deep-ink/50">Permanently remove this staff account after confirmation.</p>
                        </div>
                        <Button type="button" variant="outline" onClick={() => deleteUser(staff)} disabled={deletingUserId === staff.id} data-testid={`delete-staff-user-${staff.id}`} className="gap-2 rounded-full border-terracotta/30 bg-warm-ivory text-terracotta hover:bg-terracotta hover:text-warm-ivory">
                          <Trash2 className="h-4 w-4" />{deletingUserId === staff.id ? "Deleting…" : "Delete user"}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {!loadingUsers && users.length > 0 && filteredUsers.length === 0 && <p className="text-sm text-deep-ink/40">No users match the selected filters.</p>}
                  {!loadingUsers && users.length === 0 && <p className="text-sm text-deep-ink/40">No practice users found.</p>}
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const PasswordRules = ({ password = "", confirm = null, person = {}, showMatch = false, compact = false }) => {
  const lowered = password.toLowerCase();
  const emailName = (person.email || "").split("@")[0]?.toLowerCase();
  const nameConflict = (person.name || "").toLowerCase().split(/\s+/).some((part) => part.length >= 4 && lowered.includes(part));
  const checks = [
    ...PASSWORD_RULES.map((rule) => ({ ...rule, ok: rule.check(password) })),
    { id: "no-email", label: "Does not include the email name", ok: !(emailName && emailName.length >= 4 && lowered.includes(emailName)) },
    { id: "no-name", label: "Does not include the user's name", ok: !nameConflict },
    ...(showMatch ? [{ id: "match", label: "Repeated password matches", ok: password.length > 0 && password === confirm }] : []),
  ];
  return (
    <div className={`${compact ? "rounded-xl bg-warm-ivory px-3 py-2" : "rounded-2xl bg-warm-ivory p-4"}`}>
      <p className="text-xs font-semibold uppercase tracking-editorial text-deep-ink/50">Password rules</p>
      <div className={`mt-2 grid gap-1 ${compact ? "sm:grid-cols-2" : ""}`}>
        {checks.map((rule) => (
          <p key={rule.id} data-testid={`password-rule-${rule.id}`} className={`text-xs ${rule.ok ? "text-muted-sage" : "text-deep-ink/55"}`}>• {rule.label}</p>
        ))}
      </div>
    </div>
  );
};

const LabeledText = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <p className="mb-1.5 text-sm text-deep-ink/70">{label}</p>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="bg-warm-ivory" required />
  </div>
);

const LabeledPassword = ({ label, value, onChange, testid }) => (
  <LabeledText label={label} type="password" value={value} onChange={onChange} testid={testid} />
);
